import {
	AfterViewInit,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	NgZone,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
	ViewEncapsulation,
	inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import _ from 'lodash';
import { MediaShareDetail } from 'src/app/components/company/models/new/media-share-detail';
import { MediaShareDetailMapper } from 'src/app/components/company/services/media-share-detail-mapper';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { ImageService } from 'src/app/services/image.service';
import { SharedQueries } from 'src/app/shared/types/get-query';

type GalleryShot = {
	key: string;
	src: string;
	video: boolean;
	label: string;
};

type GalleryProduct = {
	modelId: number;
	anchorId: string;
	code: string;
	name: string;
	brand: string;
	collection: string;
	styleGroup: string;
	shots: GalleryShot[];
	columns: GalleryShot[][];
};

type MenuStyleGroup = { key: string; label: string; products: GalleryProduct[] };
type MenuCollection = { key: string; label: string; styleGroups: MenuStyleGroup[] };
type MenuBrand = { key: string; label: string; collections: MenuCollection[] };

const PRODUCTS_PER_BATCH = 4;

/**
 * Pagina pubblica raggiunta dal link di Media sharing.
 * Usa ViewEncapsulation.ShadowDom per non ereditare nessuna regola grafica globale dell'applicazione:
 * tutti gli stili della pagina vivono in share-media.component.scss.
 */
@Component({
	selector: 'bt-share-media',
	standalone: false,
	templateUrl: './share-media.component.html',
	styleUrl: './share-media.component.scss',
	encapsulation: ViewEncapsulation.ShadowDom
})
export class ShareMediaComponent implements OnInit, AfterViewInit, OnDestroy {
	readonly #route = inject(ActivatedRoute);
	readonly #graphqlService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #mediaShareDetailMapper = inject(MediaShareDetailMapper);
	readonly #imageService = inject(ImageService);
	readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
	readonly #zone = inject(NgZone);

	@ViewChildren('reveal') revealElements!: QueryList<ElementRef<HTMLElement>>;
	@ViewChildren('productSection') productSections!: QueryList<ElementRef<HTMLElement>>;
	@ViewChildren('loadMoreSentinel') loadMoreSentinels!: QueryList<ElementRef<HTMLElement>>;

	id = '';
	loading = false;
	downloading = false;
	enabled = true;

	products: GalleryProduct[] = [];
	menu: MenuBrand[] = [];
	allShots: GalleryShot[] = [];
	renderedCount = PRODUCTS_PER_BATCH;

	collapsedMenuKeys = new Set<string>();
	activeModelId: number | null = null;
	menuOpen = false;

	loadedShots = new Set<string>();
	failedShots = new Set<string>();
	lightboxIndex: number | null = null;

	#revealObserver?: IntersectionObserver;
	#sectionObserver?: IntersectionObserver;
	#loadMoreObserver?: IntersectionObserver;
	#resizeObserver?: ResizeObserver;
	#columnCount = 4;

	get renderedProducts(): GalleryProduct[] {
		return this.products.slice(0, this.renderedCount);
	}

	get hasMoreProducts(): boolean {
		return this.renderedCount < this.products.length;
	}

	get lightboxShot(): GalleryShot | null {
		return this.lightboxIndex === null ? null : (this.allShots[this.lightboxIndex] ?? null);
	}

	ngOnInit(): void {
		this.id = this.#route.snapshot.paramMap.get('id') ?? '';
		this.getMedia(this.id);
	}

	ngAfterViewInit(): void {
		const root = this.#host.nativeElement;

		this.#resizeObserver = new ResizeObserver(([entry]) => {
			const width = entry.contentRect.width;
			const columnCount = this.getColumnCount(width > 860 ? width - 300 : width);
			if (columnCount !== this.#columnCount) {
				this.#columnCount = columnCount;
				this.#zone.run(() => this.products.forEach((product) => (product.columns = this.splitInColumns(product.shots))));
			}
		});
		this.#resizeObserver.observe(root);

		this.#revealObserver = new IntersectionObserver(
			(entries) =>
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('is-visible');
						this.#revealObserver?.unobserve(entry.target);
					}
				}),
			{ root, rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
		);

		this.#sectionObserver = new IntersectionObserver(
			(entries) =>
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.#zone.run(() => (this.activeModelId = Number((entry.target as HTMLElement).dataset['modelId'])));
					}
				}),
			{ root, rootMargin: '-35% 0px -60% 0px' }
		);

		this.#loadMoreObserver = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting) && this.hasMoreProducts) {
					this.#zone.run(() => (this.renderedCount = Math.min(this.renderedCount + PRODUCTS_PER_BATCH, this.products.length)));
				}
			},
			{ root, rootMargin: '0px 0px 600px 0px' }
		);

		this.revealElements.changes
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => this.observeAll(this.revealElements, this.#revealObserver));
		this.productSections.changes
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => this.observeAll(this.productSections, this.#sectionObserver));
		this.loadMoreSentinels.changes
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => this.observeAll(this.loadMoreSentinels, this.#loadMoreObserver));
	}

	ngOnDestroy(): void {
		this.#revealObserver?.disconnect();
		this.#sectionObserver?.disconnect();
		this.#loadMoreObserver?.disconnect();
		this.#resizeObserver?.disconnect();
	}

	getMedia(id: string): void {
		this.loading = true;
		this.#graphqlService
			.getData(SharedQueries.GET_MEDIA_SHARE_DETAILS, { mediaShareLinkId: id })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['mediaShareLinkDetails'])
			)
			.subscribe({
				next: (media) => {
					this.enabled = media?.enabled !== false;
					const details = this.enabled ? this.#mediaShareDetailMapper.toMediaShareDetails(media?.styles) : [];
					this.buildGallery(details);
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	downloadImagesByMediaShareLinkId(): void {
		if (!this.id || this.downloading) {
			return;
		}

		this.downloading = true;
		this.#imageService
			.downloadImagesByMediaShareLinkId(this.id)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: Blob) => {
					this.downloading = false;
					const url = window.URL.createObjectURL(res);
					const a = document.createElement('a');
					a.href = url;
					a.download = `Images_MediaShareLink_${this.id}.zip`;
					a.click();
					window.URL.revokeObjectURL(url);
				},
				error: () => {
					this.downloading = false;
				}
			});
	}

	scrollToProduct(product: GalleryProduct): void {
		const index = this.products.indexOf(product);
		if (index >= this.renderedCount) {
			this.renderedCount = Math.min(index + PRODUCTS_PER_BATCH, this.products.length);
		}

		this.menuOpen = false;
		this.activeModelId = product.modelId;

		// attende il render delle sezioni appena aggiunte prima di scrollare
		setTimeout(() => {
			this.#host.nativeElement.shadowRoot
				?.getElementById(product.anchorId)
				?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	}

	toggleMenuNode(key: string): void {
		if (this.collapsedMenuKeys.has(key)) {
			this.collapsedMenuKeys.delete(key);
		} else {
			this.collapsedMenuKeys.add(key);
		}
	}

	isMenuNodeOpen(key: string): boolean {
		return !this.collapsedMenuKeys.has(key);
	}

	onShotLoaded(shot: GalleryShot): void {
		this.loadedShots.add(shot.key);
	}

	onShotFailed(shot: GalleryShot): void {
		this.failedShots.add(shot.key);
	}

	openLightbox(shot: GalleryShot): void {
		this.lightboxIndex = this.allShots.indexOf(shot);
	}

	closeLightbox(): void {
		this.lightboxIndex = null;
	}

	showPreviousShot(): void {
		if (this.lightboxIndex === null || !this.allShots.length) return;
		this.lightboxIndex = (this.lightboxIndex - 1 + this.allShots.length) % this.allShots.length;
	}

	showNextShot(): void {
		if (this.lightboxIndex === null || !this.allShots.length) return;
		this.lightboxIndex = (this.lightboxIndex + 1) % this.allShots.length;
	}

	@HostListener('document:keydown', ['$event'])
	onKeydown(event: KeyboardEvent): void {
		if (this.lightboxIndex === null) return;

		if (event.key === 'Escape') this.closeLightbox();
		if (event.key === 'ArrowLeft') this.showPreviousShot();
		if (event.key === 'ArrowRight') this.showNextShot();
	}

	private buildGallery(details: MediaShareDetail[]): void {
		const sortKey = (p: GalleryProduct) => `${p.brand}|${p.collection}|${p.styleGroup}|${p.code}`;

		this.products = details
			.map((detail) => {
				const style = detail.style;
				const product: GalleryProduct = {
					modelId: style.modelId ?? 0,
					anchorId: `product-${style.modelId}`,
					code: style.code ?? '',
					name: style.name ?? '',
					brand: style.brand?.name?.trim() || 'Other brands',
					collection: style.collection?.name?.trim() || 'No collection',
					styleGroup: style.styleGroup?.name?.trim() || 'No style group',
					shots: [],
					columns: []
				};

				detail.colors.forEach((color) =>
					color.shots
						.filter((shot) => !!shot.imgPath)
						.forEach((shot) => {
							const src = shot.imgPath!;
							const path = src.toLowerCase();
							product.shots.push({
								key: `${style.modelId}-${color.color.id}-${shot.id}`,
								src,
								video: path.endsWith('.mp4') || path.endsWith('.mkv'),
								label: [color.color.code, color.color.name, shot.shotType?.name].filter(Boolean).join(' · ')
							});
						})
				);

				product.columns = this.splitInColumns(product.shots);
				return product;
			})
			.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

		this.allShots = _.flatten(this.products.map((product) => product.shots));
		this.menu = this.buildMenu(this.products);
		this.renderedCount = Math.min(PRODUCTS_PER_BATCH, this.products.length);
		this.activeModelId = this.products[0]?.modelId ?? null;
	}

	private buildMenu(products: GalleryProduct[]): MenuBrand[] {
		const brands: MenuBrand[] = [];

		products.forEach((product) => {
			let brand = brands.find((b) => b.label === product.brand);
			if (!brand) {
				brand = { key: `b:${product.brand}`, label: product.brand, collections: [] };
				brands.push(brand);
			}

			let collection = brand.collections.find((c) => c.label === product.collection);
			if (!collection) {
				collection = { key: `${brand.key}/c:${product.collection}`, label: product.collection, styleGroups: [] };
				brand.collections.push(collection);
			}

			let styleGroup = collection.styleGroups.find((g) => g.label === product.styleGroup);
			if (!styleGroup) {
				styleGroup = { key: `${collection.key}/g:${product.styleGroup}`, label: product.styleGroup, products: [] };
				collection.styleGroups.push(styleGroup);
			}

			styleGroup.products.push(product);
		});

		return brands;
	}

	/** Masonry: la foto i-esima va nella colonna i % n, cosi' l'ordine resta da sinistra a destra. */
	private splitInColumns(shots: GalleryShot[]): GalleryShot[][] {
		const columns: GalleryShot[][] = Array.from({ length: this.#columnCount }, () => []);
		shots.forEach((shot, index) => columns[index % this.#columnCount].push(shot));
		return columns;
	}

	private getColumnCount(width: number): number {
		if (width >= 1300) return 4;
		if (width >= 800) return 3;
		return 2;
	}

	private observeAll(list: QueryList<ElementRef<HTMLElement>>, observer?: IntersectionObserver): void {
		if (!observer) return;
		list.forEach((el) => observer.observe(el.nativeElement));
	}
}
